import { Outlet, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ProjectProvider } from '@/context/ProjectContext';
import { AppShell } from '@/layouts/AppShell';
import { StateCard } from '@/components/StateCard';
import { SkeletonRow } from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { useGetCurrentUserQuery, useListProjectsQuery } from '@/services/api';

export function ProjectGuard(): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const projectsQ = useListProjectsQuery();
  const userQ = useGetCurrentUserQuery();

  if (projectsQ.isLoading || userQ.isLoading) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (projectsQ.isError || userQ.isError || !userQ.data) {
    return (
      <div className="content" style={{ paddingTop: 48 }}>
        <ErrorState
          title="Couldn't load your workspace"
          body="The project service is unreachable."
          onRetry={() => {
            void projectsQ.refetch();
            void userQ.refetch();
          }}
        />
      </div>
    );
  }

  const user = userQ.data;
  const projects = projectsQ.data?.projects ?? [];

  if (projects.length === 0) {
    return (
      <AppShell project={null} user={user} crumbs={[{ label: 'Choose a project' }]}>
        <StateCard variant="empty" title="No projects assigned yet — contact your administrator.">
          <p className="empty-body" style={{ fontSize: 13 }}>
            Once an admin adds you to a project, it will appear here.
          </p>
        </StateCard>
      </AppShell>
    );
  }

  const project = projects.find((p) => p.slug === projectSlug) ?? null;

  if (!project) {
    return (
      <AppShell project={null} user={user} crumbs={[{ label: 'No access' }]}>
        <StateCard variant="forbidden" title="You don't have access to this project — contact your administrator.">
          <Link className="btn" to="/p">
            ← Back to project picker
          </Link>
        </StateCard>
      </AppShell>
    );
  }

  return (
    <ProjectProvider value={{ project, projects, user }}>
      <Outlet />
    </ProjectProvider>
  );
}
